import { useAuth0 } from "@auth0/auth0-react";
import { Bars3Icon, ChevronLeftIcon } from "@heroicons/react/24/outline";
import { useEffect, useMemo, useRef, useState } from "react";
import PhoneInput from "react-phone-input-2";
import { useLocation, useNavigate } from "react-router-dom";
import { BetaFeedbackModal } from "../components/BetaFeedbackModal";
import { SideMenu } from "../components/SideMenu";
import { useCountryDetection } from "../hooks/useCountryDetection";
import { useAnalytics } from "../lib/analytics";
import { performLogout } from "../lib/auth-logout";
import { useClientTranslations } from "../lib/i18n/client";
import { getBetaTagClassName, getEnvironment } from "../lib/utils/environment";
import { TwoFactorAnalytics } from "../services/twoFactorAnalytics";
import { twoFactorService } from "../services/twoFactorService";
import { type CodeArray, clearCode } from "../utils/codeInputHelpers";
import { validatePhoneNumber } from "../utils/phoneValidation";
import {
	determineDefault2FAMethod,
	getCodeString,
	isCodeComplete,
	maskEmail,
} from "../utils/twoFactorHelpers";
import styles from "./TwoFactor.module.css";
import "../styles/phone-input-rtl.css";

type VerificationMethod = "phone" | "email";
type SmsPhase = "phone_input" | "code_input";

export function TwoFactor() {
	const location = useLocation();
	const navigate = useNavigate();
	const { user, logout } = useAuth0();
	const analytics = useAnalytics();
	const { t } = useClientTranslations("twoFactor");
	const twoFactorAnalytics = useMemo(
		() => new TwoFactorAnalytics(analytics, user),
		[analytics, user],
	);

	const environment = useMemo(() => getEnvironment(), []);
	const betaBadgeClassName = useMemo(
		() => getBetaTagClassName(environment),
		[environment],
	);

	// Auto-detect user's country for phone input
	const { countryCode: detectedCountry } = useCountryDetection();

	const searchParams = new URLSearchParams(location.search);
	const rawCallbackUrl = searchParams.get("callbackUrl") || "/";
	// Validate callbackUrl is an internal relative path to prevent open redirect
	const callbackUrl =
		rawCallbackUrl.startsWith("/") && !rawCallbackUrl.startsWith("//")
			? rawCallbackUrl
			: "/";

	const [method, setMethod] = useState<VerificationMethod>("email");
	const [isLoadingDefaultMethod, setIsLoadingDefaultMethod] = useState(true);
	const [code, setCode] = useState<CodeArray>(clearCode());
	const [isLoading, setIsLoading] = useState(false);
	const [isValidating, setIsValidating] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [isInvalidCode, setIsInvalidCode] = useState(false);
	const [successMessage, setSuccessMessage] = useState<string | null>(null);
	const [maskedContact, setMaskedContact] = useState<string>("");

	// SMS-specific states
	const [smsPhase, setSmsPhase] = useState<SmsPhase>("phone_input");
	const [phoneNumber, setPhoneNumber] = useState("");
	const [isPhoneValid, setIsPhoneValid] = useState(false);

	// Cache whether user has phone (to avoid repeated API calls)
	const [userHasPhone, setUserHasPhone] = useState(false);
	const [toast, setToast] = useState<{
		type: "success" | "error";
		message: string;
	} | null>(null);
	const [isBetaFeedbackOpen, setIsBetaFeedbackOpen] = useState(false);
	const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
	const [isMenuOpen, setIsMenuOpen] = useState(false);

	// Track whether we've verified that the user actually needs 2FA
	// This prevents the 2FA UI from flashing before we know if it's needed
	const [isVerifying2FANeeded, setIsVerifying2FANeeded] = useState(true);

	const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

	// Helper to show toast messages
	const showToast = (
		type: "success" | "error",
		message: string,
		duration = 3000,
	) => {
		setToast({ type, message });
		setTimeout(() => setToast(null), duration);
	};

	// Check if user actually needs 2FA (for returning users who land directly on this page)
	// This prevents the 2FA page from flashing when user doesn't need 2FA
	const hasChecked2FA = useRef(false);
	useEffect(() => {
		// Only check once to prevent loops on API failure
		if (!user || hasChecked2FA.current) return;
		hasChecked2FA.current = true;

		const checkIfStillNeeds2FA = async () => {
			try {
				const needs2FA = await twoFactorService.checkUserNeeds2FA();
				if (!needs2FA) {
					// User doesn't need 2FA - redirect immediately without showing UI
					navigate(callbackUrl, { replace: true });
				} else {
					// User needs 2FA - show the form
					setIsVerifying2FANeeded(false);
				}
			} catch (error) {
				// On error, assume 2FA is needed (safer default) and show the form
				console.error("Failed to check 2FA status:", error);
				setIsVerifying2FANeeded(false);
			}
		};

		checkIfStillNeeds2FA();
	}, [user, callbackUrl, navigate]);

	useEffect(() => {
		// Analytics: Track 2FA page view (deferred to avoid state update during render)
		const timer = setTimeout(() => {
			twoFactorAnalytics.trackPageView(callbackUrl);
		}, 0);
		return () => clearTimeout(timer);
	}, [callbackUrl, twoFactorAnalytics]);

	// Determine default 2FA method based on user's phone number
	const hasInitializedMethod = useRef(false);
	useEffect(() => {
		// Only run once when user is available
		if (!user || hasInitializedMethod.current) {
			return;
		}
		hasInitializedMethod.current = true;

		const initializeDefaultMethod = async () => {
			try {
				const result = await determineDefault2FAMethod(
					user,
					analytics.logUserAction,
				);
				setMethod(result.defaultMethod);

				// Cache whether user has phone to avoid repeated API calls
				const hasPhone = result.reason === "has_phone";
				setUserHasPhone(hasPhone);

				// If default method is phone and user has phone number, go to code input phase
				// If default method is phone but no phone number, stay in phone input phase
				if (result.defaultMethod === "phone") {
					if (hasPhone) {
						// User has phone number in DB - go directly to code input
						// Server will lookup phone number securely
						setSmsPhase("code_input");
					} else {
						setSmsPhase("phone_input");
					}
				}
			} catch (error) {
				// Default to email on error
				console.error("Failed to determine default 2FA method:", error);
				setMethod("email");
			} finally {
				setIsLoadingDefaultMethod(false);
			}
		};

		initializeDefaultMethod();
	}, [user, analytics.logUserAction]);

	// Timer countdown effect
	useEffect(() => {
		if (timeRemaining === null || timeRemaining <= 0) return;

		const interval = setInterval(() => {
			setTimeRemaining((prev) => {
				if (prev === null || prev <= 1) {
					clearInterval(interval);
					return null;
				}
				return prev - 1;
			});
		}, 1000);

		return () => clearInterval(interval);
	}, [timeRemaining]);

	// Format time remaining as MM:SS
	const formatTime = (seconds: number): string => {
		const mins = Math.floor(seconds / 60);
		const secs = seconds % 60;
		return `${mins}:${secs.toString().padStart(2, "0")}`;
	};

	// Phone number validation and handling

	const handlePhoneNumberChange = (value: string) => {
		setPhoneNumber(value);
		const isValid = validatePhoneNumber(value);
		setIsPhoneValid(isValid);
		setError(null);
		setSuccessMessage(null);

		// Analytics: Track phone number input
		twoFactorAnalytics.trackPhoneInput(!!value, isValid);
	};

	const handleMethodToggle = (newMethod: VerificationMethod) => {
		// Analytics: Track method selection
		twoFactorAnalytics.trackMethodSelection(method, newMethod);

		setMethod(newMethod);
		setCode(["", "", "", "", "", ""]);
		setError(null);
		setIsInvalidCode(false);
		setSuccessMessage(null);
		setMaskedContact("");
		setTimeRemaining(null);

		// Set SMS phase based on cached userHasPhone state (no API call needed)
		if (newMethod === "phone") {
			// If user has phone, go directly to code input phase
			// Otherwise, stay in phone input phase to collect phone number
			setSmsPhase(userHasPhone ? "code_input" : "phone_input");
		}
	};

	const handleCodeChange = (index: number, value: string) => {
		if (!/^\d*$/.test(value)) return;

		// Analytics: Track code input (only on first digit to avoid spam)
		if (index === 0 && value && code[0] === "") {
			twoFactorAnalytics.trackCodeInputStarted(method, "typing");
		}

		if (value.length > 1) {
			const digits = value.slice(0, 6).split("");
			const newCode = [...code];

			digits.forEach((digit, i) => {
				if (index + i < 6) {
					newCode[index + i] = digit;
				}
			});

			setCode(newCode);
			setError(null);
			setIsInvalidCode(false);

			const nextEmptyIndex = newCode.findIndex((d) => !d);
			if (nextEmptyIndex !== -1) {
				inputRefs.current[nextEmptyIndex]?.focus();
			} else {
				inputRefs.current[5]?.focus();
				if (isCodeComplete(newCode)) {
					const fullCode = getCodeString(newCode);
					// Track code complete BEFORE verification
					twoFactorAnalytics.trackCodeComplete(method);
					handleVerifyCode(fullCode);
				}
			}
			return;
		}

		const newCode = [...code];
		newCode[index] = value;
		setCode(newCode);
		setError(null);
		setIsInvalidCode(false);

		if (value && index < 5) {
			inputRefs.current[index + 1]?.focus();
		} else if (value && index === 5) {
			const fullCode = getCodeString([...newCode.slice(0, 5), value]);
			if (isCodeComplete([...newCode.slice(0, 5), value])) {
				// Track code complete BEFORE verification
				twoFactorAnalytics.trackCodeComplete(method);
				handleVerifyCode(fullCode);
			}
		}
	};

	const handleCodeKeyDown = (
		index: number,
		e: React.KeyboardEvent<HTMLInputElement>,
	) => {
		if (e.key === "Backspace" && !code[index] && index > 0) {
			inputRefs.current[index - 1]?.focus();
		}
	};

	const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
		e.preventDefault();
		const pastedData = e.clipboardData.getData("text").trim();
		const digits = pastedData.replace(/\D/g, "").slice(0, 6);

		// Analytics: Track paste event
		twoFactorAnalytics.trackCodeInputStarted(method, "paste", digits.length);

		if (digits.length > 0) {
			const newCode = [...code];
			const digitsArray = digits.split("");

			digitsArray.forEach((digit, i) => {
				if (i < 6) {
					newCode[i] = digit;
				}
			});

			setCode(newCode);
			setError(null);
			setIsInvalidCode(false);

			if (digitsArray.length === 6) {
				inputRefs.current[5]?.focus();
				// Track code complete BEFORE verification
				twoFactorAnalytics.trackCodeComplete(method);
				handleVerifyCode(digits);
			} else {
				inputRefs.current[digitsArray.length]?.focus();
			}
		}
	};

	const handleRequestCode = async () => {
		if (!user?.email) {
			const errorMsg = t("errors.userEmailNotFound");
			setError(errorMsg);
			twoFactorAnalytics.trackError("no_email", method);
			return;
		}

		if (method === "phone") {
			// Handle SMS flow based on current phase
			if (smsPhase === "phone_input") {
				// Phase 1: Validate phone number and send SMS
				if (!phoneNumber || !isPhoneValid) {
					const errorMsg = t("errors.invalidPhone");
					setError(errorMsg);
					twoFactorAnalytics.trackRequestFailure(
						"invalid_phone",
						method,
						errorMsg,
						phoneNumber,
					);
					return;
				}

				// Call SMS API to send code and save phone number
				setIsLoading(true);
				try {
					const response = await twoFactorService.requestOTP(
						"phone",
						phoneNumber,
					);
					const maskedPhone = response.maskedPhoneNumber || "(***)";
					twoFactorAnalytics.trackSmsSent(maskedPhone);
					setSmsPhase("code_input");
					setMaskedContact(maskedPhone);
					setSuccessMessage(
						t("messages.smsSent", { phoneNumber: maskedPhone }),
					);
					setTimeRemaining(300);
					showToast("success", t("messages.codeSuccess"));
				} catch (error) {
					let errorMsg = t("errors.sendSmsFailed");
					if (error instanceof Error) {
						// Handle specific backend error messages
						if (
							error.message.includes(
								"Phone number is required for SMS delivery",
							)
						) {
							errorMsg = t("errors.phoneNumberRequired");
						} else {
							errorMsg = error.message;
						}
					}
					setError(errorMsg);
					twoFactorAnalytics.trackRequestFailure(
						"sms_send_failed",
						method,
						errorMsg,
						phoneNumber,
					);
					showToast("error", errorMsg, 5000);
				} finally {
					setIsLoading(false);
				}
				return;
			} else if (smsPhase === "code_input") {
				// Phase 2: Send SMS to phone number stored in DB (server-side lookup)
				setIsLoading(true);
				try {
					const response = await twoFactorService.requestOTP("phone"); // No phone number - server looks up from DB
					const maskedPhone = response.maskedPhoneNumber || "(masked)";
					twoFactorAnalytics.trackSmsSent(maskedPhone);
					setMaskedContact(maskedPhone);
					setSuccessMessage(
						t("messages.smsSent", { phoneNumber: maskedPhone }),
					);
					setTimeRemaining(300);
					showToast("success", t("messages.codeSuccess"));
				} catch (error) {
					let errorMsg = t("errors.sendSmsFailed");
					if (error instanceof Error) {
						// Handle specific backend error messages
						if (
							error.message.includes(
								"Phone number is required for SMS delivery",
							)
						) {
							errorMsg = t("errors.phoneNumberRequired");
						} else {
							errorMsg = error.message;
						}
					}
					setError(errorMsg);
					twoFactorAnalytics.trackRequestFailure(
						"sms_send_failed",
						method,
						errorMsg,
						"(masked)",
					);
					showToast("error", errorMsg, 5000);
				} finally {
					setIsLoading(false);
				}
				return;
			}
		}

		// Analytics: Track code request attempt
		twoFactorAnalytics.trackCodeRequested(method);

		setIsLoading(true);
		setError(null);
		setIsInvalidCode(false);
		setSuccessMessage(null);

		try {
			await twoFactorService.requestOTP(method);
			twoFactorAnalytics.trackCodeSent(method);

			const maskedEmail = maskEmail(user.email);
			setMaskedContact(maskedEmail);
			setSuccessMessage(t("messages.emailSent", { email: maskedEmail }));
			setTimeRemaining(300);
			showToast("success", t("messages.codeSuccess"));
		} catch (err) {
			const errorMessage =
				err instanceof Error ? err.message : t("errors.sendFailed");

			twoFactorAnalytics.trackRequestFailure("api_error", method, errorMessage);
			setError(errorMessage);
		} finally {
			setIsLoading(false);
		}
	};

	const handleCancelAndSignOut = async () => {
		// Analytics: Track user canceling 2FA
		twoFactorAnalytics.trackCancellation(method, callbackUrl);

		await performLogout(logout);
	};

	// Helper to determine the current title and subtitle based on state
	const getTitleAndSubtitle = () => {
		// State 3: Invalid code error
		if (isInvalidCode) {
			return {
				title: t("errors.invalidCodeTitle"),
				subtitle: t("errors.invalidCodeSubtitle"),
				isError: true,
			};
		}

		// State 2: Code sent successfully
		if (successMessage && maskedContact) {
			return {
				title:
					method === "email"
						? t("codeSent.emailTitle")
						: t("codeSent.phoneTitle"),
				subtitle: t("codeSent.subtitle", { contact: maskedContact }),
				isError: false,
			};
		}

		// State 1: Initial state
		return {
			title: t("title"),
			subtitle: t("subtitle"),
			isError: false,
		};
	};

	const { title, subtitle, isError } = getTitleAndSubtitle();

	const handleVerifyCode = async (fullCode: string) => {
		if (!user?.email) {
			const errorMsg = t("errors.userEmailNotFound");
			setError(errorMsg);
			twoFactorAnalytics.trackError("no_email", method);
			return;
		}

		// Analytics: Track verification attempt
		twoFactorAnalytics.trackVerificationStarted(method, fullCode.length);

		setIsValidating(true);
		setError(null);
		setIsInvalidCode(false);

		try {
			await twoFactorService.verifyOTP(fullCode, method);

			// Analytics: Track successful verification
			twoFactorAnalytics.trackVerificationSuccess(method, callbackUrl);

			showToast("success", t("messages.verificationSuccess"));

			await twoFactorService.complete2FA();

			// Analytics: Track 2FA completion
			twoFactorAnalytics.trackCompletion(method, callbackUrl);

			// Fetch fresh status to determine where to navigate
			// This handles the post-registration case where JWT claims may be missing
			try {
				const statusResponse = await twoFactorService.getUserStatus();
				console.log("🔐 [2FA] Fresh status after completion:", statusResponse);

				setTimeout(() => {
					if (statusResponse.needs_onboarding) {
						console.log(
							"🔐 [2FA] User needs onboarding, navigating to /onboarding",
						);
						twoFactorAnalytics.trackRedirect(
							method,
							"/onboarding",
							"needs_onboarding",
						);
						navigate("/onboarding", { replace: true });
					} else {
						console.log("🔐 [2FA] User onboarded, navigating to", callbackUrl);
						twoFactorAnalytics.trackRedirect(
							method,
							callbackUrl,
							"callback_url",
						);
						navigate(callbackUrl, { replace: true });
					}
				}, 1000);
			} catch (statusError) {
				// If status check fails, fall back to callbackUrl
				console.warn(
					"🔐 [2FA] Failed to get status, using callbackUrl:",
					statusError,
				);
				setTimeout(() => {
					twoFactorAnalytics.trackRedirect(method, callbackUrl, "fallback");
					navigate(callbackUrl, { replace: true });
				}, 1000);
			}
		} catch (err) {
			const errorMessage =
				err instanceof Error ? err.message : t("errors.verificationFailed");

			if (errorMessage.includes("Invalid") || errorMessage.includes("code")) {
				// Analytics: Track invalid code error
				twoFactorAnalytics.trackVerificationFailure(
					"invalid_code",
					method,
					errorMessage,
				);

				setIsInvalidCode(true);
				setTimeout(() => {
					setCode(["", "", "", "", "", ""]);
					inputRefs.current[0]?.focus();
				}, 1500);
			} else {
				// Analytics: Track network/API error
				twoFactorAnalytics.trackVerificationFailure(
					"network_error",
					method,
					errorMessage,
				);

				setError(
					t("errors.networkError", {
						message:
							err instanceof Error ? err.message : t("errors.pleaseTryAgain"),
					}),
				);
				setCode(["", "", "", "", "", ""]);
				inputRefs.current[0]?.focus();
			}
		} finally {
			setIsValidating(false);
		}
	};

	// Don't render 2FA UI until we've verified it's actually needed
	// This prevents the 2FA page from flashing when user doesn't need 2FA
	if (isVerifying2FANeeded) {
		return null; // GlobalLoadingModal will show instead
	}

	return (
		<div className={styles.container}>
			<div className={styles.backgroundDecoration}>
				<svg
					width="615"
					height="541"
					viewBox="0 0 615 541"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
					role="img"
					aria-label="Background decoration"
				>
					<ellipse
						cx="307.5"
						cy="270.5"
						rx="307.5"
						ry="270.5"
						fill="#c1e1fc"
						opacity="0.6"
					/>
				</svg>
			</div>

			<div className={styles.mainContent}>
				{/* Navigation Bar */}
				<div className={styles.navBar}>
					<button
						type="button"
						onClick={handleCancelAndSignOut}
						className={styles.backButton}
						aria-label="Go back"
					>
						<ChevronLeftIcon className={styles.backIcon} />
					</button>

					<div className={styles.logoWithBeta}>
						<img
							src="/images/era-text-black.png"
							alt="Era"
							className={styles.navLogo}
						/>
						<button
							type="button"
							className={styles[betaBadgeClassName]}
							onClick={() => {
								setIsBetaFeedbackOpen(true);
								analytics.logEvent("beta_tag_click");
							}}
						>
							<div className={styles.betaBadgeText}>Beta</div>
						</button>
					</div>

					<button
						type="button"
						onClick={() => {
							setIsMenuOpen(true);
							analytics.logEvent("2fa_menu_click");
						}}
						className={styles.menuButton}
						aria-label="Menu"
					>
						<Bars3Icon className={styles.menuIcon} />
					</button>
				</div>

				<div className={`${styles.formSection} desktop-content-width`}>
					<div className={styles.card}>
						<div className={styles.cardTopContent}>
							<div className={styles.header}>
								<h1 className={styles.mainTitle}>{t("welcomeTitle")}</h1>
								<div className={styles.subHeader}>
									<h2
										className={`${styles.title} ${isError ? styles.titleError : ""}`}
									>
										{title}
									</h2>
									<p className={styles.subtitle}>
										{successMessage && maskedContact
											? t("codeSent.subtitle")
													.split("{{contact}}")
													.map((part, index) =>
														index === 0 ? (
															<span key={index}>{part}</span>
														) : (
															<span key={index}>
																<span className={styles.highlightedContact}>
																	{maskedContact}
																</span>
																{part}
															</span>
														),
													)
											: subtitle}
									</p>
								</div>
							</div>

							<div className={styles.authContent}>
								<div className={styles.methodToggle}>
									<button
										type="button"
										onClick={() => handleMethodToggle("phone")}
										disabled={isLoadingDefaultMethod}
										className={`${styles.methodButton} ${
											method === "phone"
												? styles.methodButtonActive
												: styles.methodButtonInactive
										}`}
									>
										{t("methods.phone")}
									</button>
									<button
										type="button"
										onClick={() => handleMethodToggle("email")}
										disabled={isLoadingDefaultMethod}
										className={`${styles.methodButton} ${
											method === "email"
												? styles.methodButtonActive
												: styles.methodButtonInactive
										}`}
									>
										{t("methods.email")}
									</button>
								</div>

								{error && <div className={styles.errorMessage}>{error}</div>}

								{/* Conditional rendering based on method and SMS phase */}
								{method === "phone" && smsPhase === "phone_input" ? (
									// SMS Phase 1: Phone number input
									<div className="phone-input-2-wrapper">
										<PhoneInput
											country={detectedCountry}
											value={phoneNumber}
											onChange={handlePhoneNumberChange}
											disabled={
												isLoading || isValidating || isLoadingDefaultMethod
											}
											placeholder={t("placeholders.phoneNumber")}
											inputProps={{
												required: true,
												autoFocus: true,
											}}
										/>
										{phoneNumber && !isPhoneValid && (
											<div className={styles.phoneValidationError}>
												{t("validation.phoneInvalid")}
											</div>
										)}
									</div>
								) : (
									// SMS Phase 2 or Email: Code input
									<div className={styles.codeInputContainer}>
										{code.map((digit, index) => (
											<input
												key={`code-input-${index}`}
												ref={(el) => {
													inputRefs.current[index] = el;
												}}
												type="text"
												inputMode="numeric"
												maxLength={6}
												autoComplete={index === 0 ? "one-time-code" : "off"}
												value={digit}
												onChange={(e) =>
													handleCodeChange(index, e.target.value)
												}
												onKeyDown={(e) => handleCodeKeyDown(index, e)}
												onPaste={handlePaste}
												disabled={isValidating}
												className={`${styles.codeInput} ${
													isInvalidCode ? styles.codeInputInvalid : ""
												}`}
												placeholder={t("placeholders.codeDigit")}
											/>
										))}
									</div>
								)}

								{/* Resend and Timer - Only show after code is sent */}
								{successMessage && (
									<div className={styles.resendTimerContainer}>
										<p className={styles.resendText}>
											{t("resend.didntGetCode")}{" "}
											<button
												type="button"
												onClick={handleRequestCode}
												disabled={isLoading}
												className={styles.resendButton}
											>
												{t("resend.resend")}
											</button>
										</p>
										{timeRemaining !== null && (
											<p className={styles.timerText}>
												{t("resend.expiresIn")} {formatTime(timeRemaining)}
											</p>
										)}
									</div>
								)}
							</div>
						</div>

						<button
							type="button"
							onClick={handleRequestCode}
							disabled={
								isLoading ||
								isValidating ||
								isLoadingDefaultMethod ||
								(method === "phone" &&
									smsPhase === "phone_input" &&
									(!phoneNumber || !isPhoneValid))
							}
							className={`${styles.requestButton} ${
								isLoading ||
								isValidating ||
								isLoadingDefaultMethod ||
								(
									method === "phone" &&
										smsPhase === "phone_input" &&
										(!phoneNumber || !isPhoneValid)
								)
									? styles.requestButtonDisabled
									: ""
							}`}
						>
							{isLoadingDefaultMethod
								? t("buttons.loading")
								: isLoading
									? t("buttons.sending")
									: isValidating
										? t("buttons.validating")
										: method === "phone" && smsPhase === "phone_input"
											? t("buttons.sendSmsCode")
											: t("buttons.requestCode")}
						</button>
					</div>

					<button
						type="button"
						onClick={handleCancelAndSignOut}
						className={styles.signOutButton}
					>
						{t("buttons.cancelSignOut")}
					</button>
				</div>
			</div>

			{toast && (
				<div
					className={`${styles.toast} ${
						toast.type === "success" ? styles.toastSuccess : styles.toastError
					}`}
				>
					{toast.message}
				</div>
			)}

			{/* Beta Feedback Modal */}
			<BetaFeedbackModal
				isOpen={isBetaFeedbackOpen}
				onClose={() => setIsBetaFeedbackOpen(false)}
			/>

			{/* Side Menu - minimal version for 2FA page */}
			<SideMenu
				isOpen={isMenuOpen}
				onClose={() => setIsMenuOpen(false)}
				minimal={true}
			/>
		</div>
	);
}
